import React, { useState, useMemo, useCallback } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { COST_CATEGORIES, SUB_ITEM_FORM_CONFIG, formatCurrency, formatMonthForDisplay } from '../constants';
import { Project, ExpenseEntry, MainCostCategory } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { SearchIcon } from '../components/icons/SearchIcon';
import { PlusIcon } from '../components/icons/PlusIcon';
import { EditIcon } from '../components/icons/EditIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { ChevronLeftIcon } from '../components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../components/icons/ChevronRightIcon';
import { DynamicEntryModal } from '../components/DynamicEntryModal';
import { EyeIcon } from '../components/icons/EyeIcon';
import { useExpenses } from '../hooks/useExpenses';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { Modal } from '../components/ui/Modal';

const ITEMS_PER_PAGE = 10;

const getBaseDescription = (desc?: string): string => {
    if (!desc) return '';
    const match = desc.match(/^(.*) \(Parcela \d+\/\d+\)$/);
    return match ? match[1].trim() : desc;
};

const getSeriesKey = (exp: ExpenseEntry): string | null => {
    if (exp.seriesId && exp.seriesId.trim()) return exp.seriesId.trim();

    const parcelMatch = exp.description?.match(/^(.*) \(Parcela \d+\/\d+\)$/);
    if (parcelMatch) {
        const baseDesc = parcelMatch[1].trim();
        return `synth_${exp.projectId}_${exp.subCategoryItemId || 'no-sub'}_${(exp.supplier || '').toLowerCase().trim()}_${baseDesc.toLowerCase()}`;
    }

    if (exp.isRecurring || exp.isInstallment || (exp.recurrenceMonths && exp.recurrenceMonths > 0) || (exp.installmentMonths && exp.installmentMonths > 0)) {
        const cleanDesc = (exp.description || '').replace(/\s*\(Parcela \d+\/\d+\)$/, '').trim().toLowerCase();
        return `synth_rec_${exp.projectId}_${exp.subCategoryItemId || 'no-sub'}_${(exp.supplier || '').toLowerCase().trim()}_${cleanDesc}`;
    }

    return null;
};

interface TableRowItem {
    type: 'single' | 'series';
    id: string;
    mainEntry: ExpenseEntry;
    children?: ExpenseEntry[];
}

export const SubItemDetailPage: React.FC = () => {
    const { obraId, categoryId, subItemId } = ReactRouterDom.useParams<{ obraId: string; categoryId: string; subItemId: string }>();
    const navigate = ReactRouterDom.useNavigate();
    const { allExpenses, saveExpense, deleteExpense, deleteExpenses } = useExpenses();
    const { projects, loading: projectsLoading } = useProjects();
    const { users } = useUsers();

    // Component-level state for UI
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState<ExpenseEntry | null>(null);
    const [expenseToDelete, setExpenseToDelete] = useState<ExpenseEntry | null>(null);
    const [expandedSeriesIds, setExpandedSeriesIds] = useState<Record<string, boolean>>({});

    // Selection and Join mechanism states
    const [selectedExpenseIds, setSelectedExpenseIds] = useState<Record<string, boolean>>({});
    const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
    const [joinBaseDescription, setJoinBaseDescription] = useState('');
    const [joinGroupType, setJoinGroupType] = useState<'recorrente' | 'parcelado'>('recorrente');
    const [isJoining, setIsJoining] = useState(false);

    // Focused editing and bulk values states
    const [editFocus, setEditFocus] = useState<'all' | 'values' | 'info'>('all');
    const [bulkNewValue, setBulkNewValue] = useState('');
    const [isSavingBulkValue, setIsSavingBulkValue] = useState(false);

    // Memoized selectors
    const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
    const isLocked = project?.isLocked;
    const config = useMemo(() => subItemId ? SUB_ITEM_FORM_CONFIG[subItemId] : null, [subItemId]);
    const categoryDetails = useMemo(() => COST_CATEGORIES.find(c => c.id === categoryId), [categoryId]);
    const subItemDetails = useMemo(() => categoryDetails?.subItems.find(si => si.id === subItemId), [categoryDetails, subItemId]);
    const userMap = useMemo(() => new Map(users.map(u => [u.id, u.name])), [users]);

    const finalTableColumns = useMemo(() => {
        if (!config?.tableColumns) return [];
        return config.tableColumns.map(col => {
            if (col.key === 'requesterId') {
                return {
                    ...col,
                    render: (value: any) => userMap.get(value) || value || 'N/A'
                };
            }
            return col;
        });
    }, [config, userMap]);

    const hasSupplierColumn = useMemo(() => finalTableColumns.some(c => c.key === 'supplier'), [finalTableColumns]);
    const shouldRenderBadgeInCol = useCallback((colKey: string) => {
        if (colKey === 'supplier') return true;
        if (colKey === 'requesterId' && !hasSupplierColumn) return true;
        return false;
    }, [hasSupplierColumn]);

    const expenses = useMemo(() => {
        if (!subItemId || !obraId) return [];
        return allExpenses
            .filter(e => e.projectId === obraId && e.subCategoryItemId === subItemId)
            .sort((a, b) => (b.month || '').localeCompare(a.month || ''));
    }, [allExpenses, obraId, subItemId]);

    const toggleSeriesExpansion = (seriesId: string) => {
        setExpandedSeriesIds(prev => ({
            ...prev,
            [seriesId]: !prev[seriesId]
        }));
    };

    const renderBadge = (entry: ExpenseEntry) => {
        const isRateio = !!entry.rateioId;
        const isRec = !!entry.isRecurring || String(entry.description || '').toLowerCase().includes('recorrente');
        const isInst = !!entry.isInstallment || String(entry.description || '').toLowerCase().includes('parcela') || !!entry.description?.match(/\(Parcela \d+\/\d+\)$/);

        const badges: React.ReactNode[] = [];

        if (isRateio) {
            const rateioExpenses = allExpenses?.filter(e => e.rateioId === entry.rateioId) || [];
            const otherProjectNames = rateioExpenses
                .filter(e => e.projectId !== obraId)
                .map(e => {
                    const proj = projects.find(p => p.id === e.projectId);
                    return proj ? proj.name : '';
                })
                .filter(Boolean);
            const uniqueOtherNames = Array.from(new Set(otherProjectNames));
            const tooltipText = uniqueOtherNames.length > 0
                ? `Rateado com: ${uniqueOtherNames.join(', ')}`
                : 'Lançamento de Rateio';

            badges.push(
                <div key="rateio" className="flex flex-col items-start">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 ml-2" title={tooltipText}>
                        <span className="material-symbols-outlined text-[13px] font-bold">donut_large</span>
                        Rateio
                    </span>
                    {uniqueOtherNames.length > 0 && (
                        <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium ml-2.5 mt-1 whitespace-normal max-w-[200px]" title={tooltipText}>
                            {uniqueOtherNames.join(', ')}
                        </span>
                    )}
                </div>
            );
        }

        if (isRec) {
            badges.push(
                <span key="rec" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 ml-2" title="Lançamento Recorrente">
                    <span className="material-symbols-outlined text-[13px] font-bold">repeat</span>
                    Recorrente
                </span>
            );
        } else if (isInst) {
            badges.push(
                <span key="inst" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800 ml-2" title="Lançamento Parcelado">
                    <span className="material-symbols-outlined text-[13px] font-bold">credit_card</span>
                    Parcelado
                </span>
            );
        } else if (!isRateio) {
            badges.push(
                <span key="ind" className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700 ml-2" title="Lançamento Individual">
                    <span className="material-symbols-outlined text-[13px] font-bold">person</span>
                    Individual
                </span>
            );
        }

        return <>{badges}</>;
    };

    const groupedRowItems = useMemo(() => {
        const items: TableRowItem[] = [];
        const seriesMap = new Map<string, ExpenseEntry[]>();

        expenses.forEach(exp => {
            const groupKey = getSeriesKey(exp);
            if (groupKey) {
                if (!seriesMap.has(groupKey)) {
                    seriesMap.set(groupKey, []);
                }
                seriesMap.get(groupKey)!.push(exp);
            }
        });

        const addedSeries = new Set<string>();

        expenses.forEach(exp => {
            const groupKey = getSeriesKey(exp);
            if (groupKey) {
                const children = seriesMap.get(groupKey)!;
                if (children.length > 1) {
                    if (!addedSeries.has(groupKey)) {
                        addedSeries.add(groupKey);
                        const sortedChildren = [...children].sort((a, b) => (a.month || '').localeCompare(b.month || ''));
                        items.push({
                            type: 'series',
                            id: groupKey,
                            mainEntry: sortedChildren[0],
                            children: sortedChildren
                        });
                    }
                } else {
                    items.push({
                        type: 'single',
                        id: exp.id,
                        mainEntry: exp
                    });
                }
            } else {
                items.push({
                    type: 'single',
                    id: exp.id,
                    mainEntry: exp
                });
            }
        });

        return items;
    }, [expenses]);

    const filteredRowItems = useMemo(() => {
        if (!searchTerm) return groupedRowItems;
        const normalizedSearch = searchTerm.toLowerCase();

        return groupedRowItems.filter(item => {
            const matchesExpense = (exp: ExpenseEntry) => {
                return Object.entries(exp).some(([key, val]) => {
                    if (key === 'attachments' || key === 'id' || key === 'projectId' || key === 'seriesId' || key === 'rateioId') return false;
                    if (val === null || val === undefined) return false;
                    return String(val).toLowerCase().includes(normalizedSearch);
                });
            };

            if (item.type === 'single') {
                return matchesExpense(item.mainEntry);
            } else {
                return matchesExpense(item.mainEntry) || (item.children?.some(matchesExpense) ?? false);
            }
        });
    }, [groupedRowItems, searchTerm]);

    const paginatedRowItems = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredRowItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredRowItems, currentPage]);

    const totalPages = Math.ceil(filteredRowItems.length / ITEMS_PER_PAGE);

    const selectedCount = useMemo(() => {
        return Object.keys(selectedExpenseIds).filter(id => selectedExpenseIds[id]).length;
    }, [selectedExpenseIds]);

    React.useEffect(() => {
        if (isJoinModalOpen) {
            const firstSelected = expenses.find(e => selectedExpenseIds[e.id]);
            if (firstSelected) {
                setJoinBaseDescription(getBaseDescription(firstSelected.description));
            }
        }
    }, [isJoinModalOpen, selectedExpenseIds, expenses]);

    const toggleSelectExpense = (id: string) => {
        setSelectedExpenseIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleSelectSeriesGroup = (seriesId: string, childrenList: ExpenseEntry[]) => {
        const allSelected = childrenList.every(child => selectedExpenseIds[child.id]);
        setSelectedExpenseIds(prev => {
            const next = { ...prev };
            childrenList.forEach(child => {
                next[child.id] = !allSelected;
            });
            return next;
        });
    };

    const paginatedFlatExpenses = useMemo(() => {
        const flat: string[] = [];
        paginatedRowItems.forEach(item => {
            if (item.type === 'single') {
                flat.push(item.mainEntry.id);
            } else {
                item.children?.forEach(child => {
                    flat.push(child.id);
                });
            }
        });
        return flat;
    }, [paginatedRowItems]);

    const isAllSelectedOnPage = useMemo(() => {
        if (paginatedFlatExpenses.length === 0) return false;
        return paginatedFlatExpenses.every(id => selectedExpenseIds[id]);
    }, [paginatedFlatExpenses, selectedExpenseIds]);

    const handleToggleSelectAllOnPage = () => {
        setSelectedExpenseIds(prev => {
            const next = { ...prev };
            if (isAllSelectedOnPage) {
                paginatedFlatExpenses.forEach(id => {
                    next[id] = false;
                });
            } else {
                paginatedFlatExpenses.forEach(id => {
                    next[id] = true;
                });
            }
            return next;
        });
    };

    const handleJoinExpenses = async () => {
        if (!joinBaseDescription.trim()) {
            alert('Por favor, informe uma descrição base.');
            return;
        }

        setIsJoining(true);

        try {
            const selectedList = expenses
                .filter(e => selectedExpenseIds[e.id])
                .sort((a, b) => (a.month || '').localeCompare(b.month || ''));

            if (selectedList.length <= 1) {
                alert('Selecione pelo menos 2 lançamentos para juntar.');
                setIsJoining(false);
                return;
            }

            const newSeriesId = `series_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            const total = selectedList.length;

            const updatePromises = selectedList.map((exp, idx) => {
                const isRec = joinGroupType === 'recorrente';
                const labelSuffix = `(Parcela ${idx + 1}/${total})`;
                const updatedDescription = `${joinBaseDescription.trim()} ${labelSuffix}`;

                return saveExpense({
                    ...exp,
                    seriesId: newSeriesId,
                    isRecurring: isRec,
                    isInstallment: !isRec,
                    description: updatedDescription,
                });
            });

            const results = await Promise.all(updatePromises);
            const failed = results.find(r => !r.success);
            if (failed) {
                throw new Error(failed.message || 'Falha ao atualizar lançamentos.');
            }

            alert('Lançamentos agrupados em série com sucesso!');
            setSelectedExpenseIds({});
            setIsJoinModalOpen(false);
            setJoinBaseDescription('');
        } catch (e) {
            console.error('Erro ao agrupar lançamentos:', e);
            alert(`Ocorreu um erro ao agrupar: ${(e as Error).message}`);
        } finally {
            setIsJoining(false);
        }
    };

    const handleOpenAddModal = () => {
        setEditFocus('all');
        setEditingExpense(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (expense: ExpenseEntry, focus: 'all' | 'values' | 'info' = 'all') => {
        setEditFocus(focus);
        setEditingExpense(expense);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingExpense(null);
    };

    const handleSaveExpense = async (dataFromModal: any) => {
        // Check if this is a recurring series extension or rateio payload
        if ('updates' in dataFromModal && 'creations' in dataFromModal) {
            handleCloseModal();
            const isRateio = dataFromModal.updates.length === 0;
            const hasDeletions = dataFromModal.deletions && dataFromModal.deletions.length > 0;

            alert(isRateio ? 'Processando o rateio...' : 'Processando a série de lançamentos...');
            try {
                // Execute deletions if any
                if (hasDeletions) {
                    await deleteExpenses(dataFromModal.deletions);
                }

                // Execute all update operations sequentially to prevent overloading server connection pool
                if (dataFromModal.updates.length > 0) {
                    for (const exp of dataFromModal.updates) {
                        const res = await saveExpense(exp);
                        if (!res.success) {
                            throw new Error(res.message || 'Falha ao atualizar lançamentos.');
                        }
                    }
                }

                // Execute creation operations sequentially
                if (dataFromModal.creations.length > 0) {
                    for (const exp of dataFromModal.creations) {
                        const res = await saveExpense([exp]);
                        if (!res.success) {
                            throw new Error(res.message || 'Falha ao salvar lançamentos.');
                        }
                    }
                }
                alert(isRateio ? 'Rateio processado com sucesso!' : 'Série de lançamentos processada com sucesso!');
            } catch (e) {
                console.error("Failed to process bulk expense operation", e);
                alert(`Ocorreu um erro ao processar a operação: ${(e as Error).message}`);
            }
            return; // End execution for bulk operations
        }

        // --- Standard save logic for single entries ---
        const payload: Partial<ExpenseEntry> = { ...dataFromModal };
        const isEditing = !!payload.id;

        if (!isEditing) {
            payload.projectId = obraId;
            payload.mainCategory = categoryId as MainCostCategory;
            payload.subCategoryItemId = subItemDetails!.id;
            payload.subCategoryItemName = subItemDetails!.name;
        }

        // Check if we are doing a focused 'info' update on a series or rateio
        if (isEditing && editFocus === 'info' && (payload.seriesId || payload.rateioId || getSeriesKey(payload as ExpenseEntry))) {
            const groupKey = getSeriesKey(payload as ExpenseEntry);
            const rateioId = payload.rateioId;
            const seriesExpenses = expenses.filter(e => {
                if (rateioId && e.rateioId === rateioId) return true;
                if (groupKey && getSeriesKey(e) === groupKey) return true;
                return false;
            });

            if (seriesExpenses.length > 1) {
                const targetSeriesId = payload.seriesId || seriesExpenses.find(e => e.seriesId)?.seriesId || `series_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                const baseDesc = getBaseDescription(payload.description);
                for (const exp of seriesExpenses) {
                    let finalDesc = exp.description;
                    if (rateioId) {
                        const percentMatch = exp.description?.match(/\(Rateio - \d+(?:\.\d+)?%\)$/);
                        if (percentMatch) {
                            finalDesc = `${payload.description || ''} ${percentMatch[0]}`.trim();
                        } else {
                            finalDesc = payload.description;
                        }
                    } else if (baseDesc) {
                        const match = exp.description?.match(/\(Parcela \d+\/\d+\)$/);
                        finalDesc = match ? `${baseDesc} ${match[0]}` : baseDesc;
                    }
                    const res = await saveExpense({
                        ...exp,
                        seriesId: targetSeriesId,
                        supplier: payload.supplier !== undefined ? payload.supplier : exp.supplier,
                        invoice: payload.invoice !== undefined ? payload.invoice : exp.invoice,
                        date: payload.date !== undefined ? payload.date : exp.date,
                        requesterId: payload.requesterId !== undefined ? payload.requesterId : exp.requesterId,
                        description: finalDesc,
                        observation: payload.observation !== undefined ? payload.observation : exp.observation,
                        attachments: payload.attachments !== undefined ? payload.attachments : exp.attachments,
                    });
                    if (!res.success) {
                        alert(`Ocorreu um erro ao atualizar os lançamentos: ${res.message}`);
                        return;
                    }
                }
                alert(rateioId ? 'Informações gerais de todos os lançamentos do rateio atualizadas com sucesso!' : 'Informações gerais de todas as parcelas da série atualizadas com sucesso!');
                handleCloseModal();
                return;
            }
        }

        const result = await saveExpense(payload);

        if (result.success) {
            handleCloseModal();
            setSearchTerm('');
            setCurrentPage(1);
        } else {
            alert(`Ocorreu uma falha ao salvar o lançamento:\n\n${result.message || 'Erro desconhecido. Verifique o console.'}`);
        }
    };

    const handleOpenDeleteModal = (expense: ExpenseEntry) => {
        setExpenseToDelete(expense);
    };

    const handleConfirmDelete = () => {
        if (expenseToDelete) {
            deleteExpense(expenseToDelete.id);
            setExpenseToDelete(null);
        }
    };

    const handleSaveBulkValue = async () => {
        const numericVal = parseFloat(bulkNewValue);
        if (isNaN(numericVal) || numericVal < 0) {
            alert('Por favor, insira um valor numérico válido.');
            return;
        }

        setIsSavingBulkValue(true);
        try {
            const selectedList = expenses.filter(e => selectedExpenseIds[e.id]);
            if (selectedList.length === 0) {
                alert('Nenhum lançamento selecionado.');
                setIsSavingBulkValue(false);
                return;
            }

            const updatePromises = selectedList.map(exp => {
                const updated: Partial<ExpenseEntry> = {
                    ...exp,
                    amount: numericVal
                };
                if (exp.quantity && exp.unitPrice) {
                    updated.quantity = 1;
                    updated.unitPrice = numericVal;
                }
                return saveExpense(updated);
            });

            const results = await Promise.all(updatePromises);
            const failed = results.find(r => !r.success);
            if (failed) {
                throw new Error(failed?.message || 'Falha ao atualizar lançamentos.');
            }

            alert('Valores atualizados em lote com sucesso!');
            setSelectedExpenseIds({});
            setBulkNewValue('');
        } catch (e) {
            console.error('Erro ao salvar valores em lote:', e);
            alert(`Ocorreu um erro ao salvar os valores: ${(e as Error).message}`);
        } finally {
            setIsSavingBulkValue(false);
        }
    };

    const handleAutoSelectAndFocusSeriesValues = (seriesId: string, childrenList: ExpenseEntry[]) => {
        // Expand the series if not expanded
        if (!expandedSeriesIds[seriesId]) {
            setExpandedSeriesIds(prev => ({ ...prev, [seriesId]: true }));
        }

        // Select all child elements of this series
        setSelectedExpenseIds(prev => {
            const next = { ...prev };
            childrenList.forEach(child => {
                next[child.id] = true;
            });
            return next;
        });

        // Set focus to the value input container
        setTimeout(() => {
            const el = document.getElementById('bulk-new-value');
            if (el) {
                el.focus();
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 150);
    };

    const handleOpenDeleteSeriesModal = async (childrenList: ExpenseEntry[]) => {
        if (window.confirm(`Você tem certeza que deseja excluir esta série inteira? Isso excluirá todas as ${childrenList.length} parcelas/recorrências de uma só vez.`)) {
            try {
                const ids = childrenList.map(c => c.id);
                await deleteExpenses(ids);
                setSelectedExpenseIds({});
                alert('Série de lançamentos excluída com sucesso!');
            } catch (e) {
                console.error("Erro ao excluir série:", e);
                alert(`Erro ao excluir: ${(e as Error).message}`);
            }
        }
    };

    const handleViewDetails = (expenseId: string) => {
        navigate(`/obras/${obraId}/custos/${categoryId}/${subItemId}/${expenseId}`);
    };

    if (projectsLoading || (!project && projects.length === 0)) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-lime mb-4"></div>
                <p>Carregando dados da obra...</p>
            </div>
        );
    }

    if (!project || !categoryDetails || !subItemDetails || !config) {
        return <div className="text-center py-10 text-gray-400">Dados de configuração ou obra não encontrados.</div>;
    }

    return (
        <div className="text-gray-900 dark:text-dark-text-primary">
            {/* Header */}
            <div className="flex items-center mb-6">
                <Button variant="ghost" size="sm" onClick={() => navigate(`/obras/${obraId}/custos/${categoryId}`)} className="p-2 mr-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary" aria-label="Voltar">
                    <ArrowLeftIcon className="text-2xl" />
                </Button>
                <div>
                    <h1 className="text-xl md:text-2xl font-semibold leading-tight">
                        {project?.name || 'Carregando Obra...'}
                    </h1>
                    <p className="text-sm md:text-base font-normal text-gray-500 dark:text-dark-text-secondary leading-tight mt-1">
                        {categoryDetails?.id?.split(' - ')[0]} &gt; <span className="text-brand-lime">{subItemDetails?.name}</span>
                    </p>
                </div>
            </div>

            {/* Main Card */}
            <Card footer={
                totalPages > 1 && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Página {currentPage} de {totalPages}</span>
                        <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                <ChevronLeftIcon className="text-xl" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                <ChevronRightIcon className="text-xl" />
                            </Button>
                        </div>
                    </div>
                )
            }>
                {/* Search and Add controls */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                    <div className="relative w-full md:max-w-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon className="text-xl text-gray-400" />
                        </div>
                        <Input id="searchEntries" type="text" placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-full" containerClassName="mb-0" />
                    </div>
                    <Button variant="primary" onClick={handleOpenAddModal} leftIcon={<PlusIcon className="text-xl" />} disabled={isLocked}>
                        Adicionar
                    </Button>
                </div>

                {selectedCount >= 1 && (
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between p-4 mb-4 bg-indigo-50 border border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900 rounded-lg text-sm text-gray-800 dark:text-dark-text-primary gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-1 gap-3">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400 text-[22px]">library_add_check</span>
                                <span>Você selecionou <strong className="text-indigo-600 dark:text-indigo-400 font-bold text-base">{selectedCount}</strong> {selectedCount === 1 ? 'lançamento' : 'lançamentos'}.</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {selectedCount > 1 && (
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-medium shadow-sm h-9"
                                        onClick={() => setIsJoinModalOpen(true)}
                                        disabled={isLocked}
                                        leftIcon={<span className="material-symbols-outlined text-[16px] mr-1">merge</span>}
                                    >
                                        Juntar em Série
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-500 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:text-dark-primary h-9"
                                    onClick={() => setSelectedExpenseIds({})}
                                >
                                    Limpar Seleção
                                </Button>
                            </div>
                        </div>

                        {/* Alterar Valor Inline */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 pt-3 lg:pt-0 border-t lg:border-t-0 lg:border-l border-indigo-200 dark:border-indigo-900 pl-0 lg:pl-4">
                            <label htmlFor="bulk-new-value" className="text-xs font-semibold text-gray-700 dark:text-dark-text-primary uppercase tracking-wider block sm:inline-block">
                                Alterar valor para:
                            </label>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <div className="relative rounded-md shadow-sm w-full sm:w-36">
                                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                                        <span className="text-gray-500 dark:text-dark-text-secondary text-xs font-semibold">R$</span>
                                    </div>
                                    <input
                                        id="bulk-new-value"
                                        type="number"
                                        step="0.01"
                                        placeholder="0,00"
                                        className="block w-full rounded border-gray-300 dark:border-dark-tertiary bg-white dark:bg-dark-secondary py-1.5 pl-8 pr-2 text-sm text-gray-950 dark:text-dark-text-primary focus:border-indigo-500 focus:ring-indigo-500"
                                        value={bulkNewValue}
                                        onChange={(e) => setBulkNewValue(e.target.value)}
                                        disabled={isLocked || isSavingBulkValue}
                                    />
                                </div>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white font-semibold shadow-sm h-9 px-3 shrink-0"
                                    onClick={handleSaveBulkValue}
                                    disabled={isLocked || !bulkNewValue || isSavingBulkValue}
                                    leftIcon={<span className="material-symbols-outlined text-[16px]">save</span>}
                                >
                                    Salvar Valor
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-tertiary">
                        <thead className="bg-gray-50 dark:bg-dark-secondary">
                            <tr>
                                <th className="px-3 py-3 text-left w-10">
                                    <input
                                        type="checkbox"
                                        checked={isAllSelectedOnPage}
                                        onChange={handleToggleSelectAllOnPage}
                                        className="h-4 w-4 rounded border-gray-300 dark:border-dark-tertiary bg-white dark:bg-dark-secondary text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                    />
                                </th>
                                {finalTableColumns.map(col => (
                                    <th key={String(col.key)} className={`px-3 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider ${col.key === 'actions' ? 'text-center' : 'text-left'}`}>{col.header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-tertiary">
                            {paginatedRowItems.length > 0 ? paginatedRowItems.map(rowItem => {
                                if (rowItem.type === 'single') {
                                    const exp = rowItem.mainEntry;
                                    return (
                                        <tr key={exp.id} className="hover:bg-gray-50 dark:hover:bg-dark-secondary cursor-pointer" onClick={() => handleViewDetails(exp.id)}>
                                            <td className="px-3 py-3 whitespace-nowrap w-10" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedExpenseIds[exp.id]}
                                                    onChange={() => toggleSelectExpense(exp.id)}
                                                    className="h-4 w-4 rounded border-gray-300 dark:border-dark-tertiary bg-white dark:bg-dark-secondary text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                />
                                            </td>
                                            {finalTableColumns.map(col => (
                                                <td key={`${exp.id}-${String(col.key)}`} className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-dark-text-secondary">
                                                    {col.key === 'actions' ? (
                                                        <div className="text-center flex justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                            <Button variant="ghost" size="sm" onClick={() => handleViewDetails(exp.id)} className="p-1" title="Visualizar"><EyeIcon className="text-xl" /></Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(exp, 'values')} className="p-1 text-teal-600 dark:text-teal-400" title="Editar Valores" disabled={isLocked}>
                                                                <span className="material-symbols-outlined text-xl">payments</span>
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(exp, 'info')} className="p-1 text-blue-600 dark:text-blue-400" title="Editar Informações Gerais" disabled={isLocked}>
                                                                <span className="material-symbols-outlined text-xl">edit_note</span>
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleOpenDeleteModal(exp)} className="p-1 text-red-500 hover:text-red-650" title="Excluir" disabled={isLocked}><TrashIcon className="text-xl" /></Button>
                                                        </div>
                                                    ) : shouldRenderBadgeInCol(String(col.key)) || col.key === 'description' ? (
                                                        <span className="truncate max-w-xs block flex items-center flex-wrap gap-1" title={col.key === 'requesterId' ? (userMap.get(exp.requesterId || '') || exp.requesterId || '') : String(exp[col.key as keyof ExpenseEntry] || '')}>
                                                            <span>{col.key === 'requesterId' ? (userMap.get(exp.requesterId || '') || exp.requesterId || '') : String(exp[col.key as keyof ExpenseEntry] || '')}</span>
                                                            {renderBadge(exp)}
                                                        </span>
                                                    ) : col.render ? (
                                                        col.render(exp[col.key as keyof ExpenseEntry], exp)
                                                    ) : (
                                                        <span className="truncate max-w-xs block" title={String(exp[col.key as keyof ExpenseEntry] || '')}>
                                                            {String(exp[col.key as keyof ExpenseEntry] || '')}
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                } else {
                                    const mainExp = rowItem.mainEntry;
                                    const seriesId = rowItem.id;
                                    const children = rowItem.children || [];
                                    const isExpanded = expandedSeriesIds[seriesId];

                                    const totalAmount = children.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

                                    const consolidatedEntry: ExpenseEntry = {
                                        ...children[0],
                                        amount: totalAmount,
                                    };

                                    return (
                                        <React.Fragment key={`series-group-${seriesId}`}>
                                            <tr
                                                className="hover:bg-gray-100 dark:hover:bg-dark-secondary cursor-pointer font-semibold bg-gray-50/75 dark:bg-dark-tertiary/40 border-l-4 border-indigo-500"
                                                onClick={() => toggleSeriesExpansion(seriesId)}
                                            >
                                                <td className="px-3 py-3 whitespace-nowrap w-10" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={children.every(child => selectedExpenseIds[child.id])}
                                                        ref={(el) => {
                                                            if (el) {
                                                                const someSelected = children.some(child => selectedExpenseIds[child.id]);
                                                                const allSelected = children.every(child => selectedExpenseIds[child.id]);
                                                                el.indeterminate = someSelected && !allSelected;
                                                            }
                                                        }}
                                                        onChange={() => toggleSelectSeriesGroup(seriesId, children)}
                                                        className="h-4 w-4 rounded border-gray-300 dark:border-dark-tertiary bg-white dark:bg-dark-secondary text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                    />
                                                </td>
                                                {finalTableColumns.map(col => (
                                                    <td key={`series-row-${seriesId}-${String(col.key)}`} className="px-3 py-3 whitespace-nowrap text-sm text-gray-800 dark:text-dark-text-primary">
                                                        {col.key === 'actions' ? (
                                                            <div className="text-center flex justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                                <Button variant="ghost" size="sm" onClick={() => toggleSeriesExpansion(seriesId)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-dark-text-secondary" title={isExpanded ? "Recolher" : "Expandir"}>
                                                                    <span className="material-symbols-outlined text-lg">{isExpanded ? 'expand_less' : 'expand_more'}</span>
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleAutoSelectAndFocusSeriesValues(seriesId, children)} className="p-1 text-teal-600 dark:text-teal-400" title="Editar Valores de Parcelas/Meses" disabled={isLocked}>
                                                                    <span className="material-symbols-outlined text-lg">payments</span>
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(mainExp, 'info')} className="p-1 text-blue-600 dark:text-blue-400" title="Editar Informações Gerais da Série" disabled={isLocked}>
                                                                    <span className="material-symbols-outlined text-lg">edit_note</span>
                                                                </Button>
                                                                <Button variant="ghost" size="sm" onClick={() => handleOpenDeleteSeriesModal(children)} className="p-1 text-red-500 hover:text-red-650" title="Excluir Série Inteira" disabled={isLocked}>
                                                                    <span className="material-symbols-outlined text-lg">delete_sweep</span>
                                                                </Button>
                                                            </div>
                                                        ) : col.key === 'month' ? (
                                                            <div className="flex items-center gap-2">
                                                                <span className={`material-symbols-outlined text-[16px] transition-transform ${isExpanded ? 'rotate-90' : ''}`}>chevron_right</span>
                                                                <span>{formatMonthForDisplay(mainExp.month)} (Série)</span>
                                                            </div>
                                                        ) : col.key === 'description' ? (
                                                            <div className="flex items-center flex-wrap gap-1">
                                                                <span className="truncate max-w-xs font-semibold" title={getBaseDescription(mainExp.description)}>{getBaseDescription(mainExp.description)} ({children.length} lançamentos)</span>
                                                                {renderBadge(mainExp)}
                                                            </div>
                                                        ) : shouldRenderBadgeInCol(String(col.key)) ? (
                                                            <span className="truncate max-w-xs block font-semibold flex items-center flex-wrap gap-1" title={col.key === 'requesterId' ? (userMap.get(mainExp.requesterId || '') || mainExp.requesterId || '') : String(mainExp.supplier || '')}>
                                                                <span>{col.key === 'requesterId' ? (userMap.get(mainExp.requesterId || '') || mainExp.requesterId || '') : String(mainExp.supplier || '')}</span>
                                                                {renderBadge(mainExp)}
                                                            </span>
                                                        ) : col.key === 'amount' ? (
                                                            <span className="font-bold text-indigo-600 dark:text-indigo-400">
                                                                {formatCurrency(totalAmount)}
                                                            </span>
                                                        ) : col.render ? (
                                                            col.render(consolidatedEntry[col.key as keyof ExpenseEntry], consolidatedEntry)
                                                        ) : (
                                                            <span className="truncate max-w-xs block" title={String(consolidatedEntry[col.key as keyof ExpenseEntry] || '')}>
                                                                {String(consolidatedEntry[col.key as keyof ExpenseEntry] || '')}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>

                                            {isExpanded && children.map(child => (
                                                <tr
                                                    key={`child-${child.id}`}
                                                    className="bg-gray-50/50 dark:bg-dark-secondary/20 hover:bg-gray-100 dark:hover:bg-dark-secondary/60 cursor-pointer border-l-4 border-indigo-400/50 border-dashed"
                                                    onClick={() => handleViewDetails(child.id)}
                                                >
                                                    <td className="px-3 py-2 whitespace-nowrap pl-6 w-10" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!selectedExpenseIds[child.id]}
                                                            onChange={() => toggleSelectExpense(child.id)}
                                                            className="h-4 w-4 rounded border-gray-300 dark:border-dark-tertiary bg-white dark:bg-dark-secondary text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                        />
                                                    </td>
                                                    {finalTableColumns.map(col => (
                                                        <td key={`child-row-${child.id}-${String(col.key)}`} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 pl-6 dark:text-dark-text-secondary">
                                                            {col.key === 'actions' ? (
                                                                <div className="text-center flex justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                                    <Button variant="ghost" size="sm" onClick={() => handleViewDetails(child.id)} className="p-1" title="Visualizar"><EyeIcon className="text-xl" /></Button>
                                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(child, 'values')} className="p-1 text-teal-600 dark:text-teal-400" title="Editar Valores" disabled={isLocked}>
                                                                        <span className="material-symbols-outlined text-xl">payments</span>
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(child, 'info')} className="p-1 text-blue-600 dark:text-blue-400" title="Editar Informações Gerais" disabled={isLocked}>
                                                                        <span className="material-symbols-outlined text-xl">edit_note</span>
                                                                    </Button>
                                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenDeleteModal(child)} className="p-1 text-red-500 hover:text-red-650" title="Excluir" disabled={isLocked}><TrashIcon className="text-xl" /></Button>
                                                                </div>
                                                            ) : col.key === 'month' ? (
                                                                <span className="text-xs font-medium text-gray-400 dark:text-dark-text-secondary italic">
                                                                    {formatMonthForDisplay(child.month)}
                                                                </span>
                                                            ) : shouldRenderBadgeInCol(String(col.key)) ? (
                                                                <span className="truncate max-w-xs block flex items-center flex-wrap gap-1 font-semibold" title={col.key === 'requesterId' ? (userMap.get(child.requesterId || '') || child.requesterId || '') : String(child[col.key as keyof ExpenseEntry] || '')}>
                                                                    <span>{col.key === 'requesterId' ? (userMap.get(child.requesterId || '') || child.requesterId || '') : String(child[col.key as keyof ExpenseEntry] || '')}</span>
                                                                    {renderBadge(child)}
                                                                </span>
                                                            ) : col.render ? (
                                                                col.render(child[col.key as keyof ExpenseEntry], child)
                                                            ) : (
                                                                <span className="truncate max-w-xs block" title={String(child[col.key as keyof ExpenseEntry] || '')}>
                                                                    {String(child[col.key as keyof ExpenseEntry] || '')}
                                                                </span>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                }
                            }) : (
                                <tr>
                                    <td colSpan={finalTableColumns.length + 1} className="text-center py-10 text-gray-500 dark:text-dark-text-secondary">
                                        {searchTerm ? "Nenhum resultado encontrado." : "Nenhum lançamento para este item."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {isModalOpen && subItemId && obraId && (
                <DynamicEntryModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSubmit={handleSaveExpense}
                    existingExpense={editingExpense}
                    subItemId={subItemId}
                    obraId={obraId}
                    categoryId={categoryId}
                    allExpensesForSeries={allExpenses}
                    isLocked={isLocked}
                    editFocus={editFocus}
                />
            )}

            {expenseToDelete && (
                <DeleteConfirmationModal
                    isOpen={!!expenseToDelete}
                    onClose={() => setExpenseToDelete(null)}
                    onConfirm={handleConfirmDelete}
                    message={`Você tem certeza que deseja excluir este lançamento? Fornecedor: ${expenseToDelete.supplier || 'N/A'}, Nota Fiscal: ${expenseToDelete.invoice || 'N/A'}.`}
                />
            )}

            {isJoinModalOpen && (
                <Modal
                    isOpen={isJoinModalOpen}
                    onClose={() => setIsJoinModalOpen(false)}
                    title="Juntar Lançamentos em Série"
                    size="md"
                >
                    <div className="space-y-5 py-2">
                        <p className="text-sm text-gray-600 dark:text-dark-text-secondary leading-relaxed">
                            Você selecionou <strong className="text-indigo-600 dark:text-indigo-400 font-semibold">{selectedCount}</strong> lançamentos. Eles serão ordenados cronologicamente e unificados em uma única série de lançamentos.
                        </p>

                        <div className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text-primary">
                                Tipo de Série
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <label className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer select-none transition-all ${joinGroupType === 'recorrente'
                                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/10 text-indigo-700 dark:text-indigo-300'
                                        : 'border-gray-200 dark:border-neutral-800 text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-neutral-850'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="joinGroupType"
                                        value="recorrente"
                                        checked={joinGroupType === 'recorrente'}
                                        onChange={() => setJoinGroupType('recorrente')}
                                        className="sr-only"
                                    />
                                    <span className="material-symbols-outlined mr-2 text-lg">repeat</span>
                                    <span className="text-sm font-medium">Recorrente</span>
                                </label>
                                <label className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer select-none transition-all ${joinGroupType === 'parcelado'
                                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/10 text-indigo-700 dark:text-indigo-300'
                                        : 'border-gray-200 dark:border-neutral-800 text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-neutral-850'
                                    }`}>
                                    <input
                                        type="radio"
                                        name="joinGroupType"
                                        value="parcelado"
                                        checked={joinGroupType === 'parcelado'}
                                        onChange={() => setJoinGroupType('parcelado')}
                                        className="sr-only"
                                    />
                                    <span className="material-symbols-outlined mr-2 text-lg">credit_card</span>
                                    <span className="text-sm font-medium">Parcelado</span>
                                </label>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="joinBaseDescription" className="block text-sm font-semibold text-gray-700 dark:text-dark-text-primary">
                                Nome/Descrição Base da Série
                            </label>
                            <Input
                                id="joinBaseDescription"
                                type="text"
                                placeholder="Ex: Aluguel Alojamento 1"
                                value={joinBaseDescription}
                                onChange={(e) => setJoinBaseDescription(e.target.value)}
                                className="w-full"
                            />
                            <p className="text-xs text-gray-400 italic mt-1">
                                As descrições individuais serão reescritas como "Descrição Base (Parcela X/Y)".
                            </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-150 dark:border-neutral-800">
                            <Button
                                variant="ghost"
                                onClick={() => setIsJoinModalOpen(false)}
                                disabled={isJoining}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleJoinExpenses}
                                disabled={isJoining}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm transition-colors"
                                leftIcon={isJoining ? null : <span className="material-symbols-outlined text-[16px] mr-1">merge</span>}
                            >
                                {isJoining ? 'Agrupando...' : 'Juntar em Série'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};